import React from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Smartphone, CheckCircle2, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GridBackground } from '@/components/GridBackground';
import { TopBanner } from '@/components/TopBanner';
import { QuickAnswerSection } from '@/components/QuickAnswerSection';
import { GateFAQSection } from '@/components/GateFAQSection';
import { Footer } from '@/components/Footer';
import { deviceGuides } from '@/data/deviceGuides';

export const DeviceSetupPage = () => {
  const { deviceId } = useParams();
  const device = deviceGuides[deviceId];

  if (!device) {
    return <div>Device not found</div>;
  }

  // Generate quick answer
  const quickAnswer = `${device.name} IPTV setup takes ${device.setupSteps.length * 2}-${device.setupSteps.length * 3} minutes. ${device.overview} Compatible apps: ${device.specifications.compatibility}. Requirements: ${device.specifications.connectivity}, ${device.specifications.resolution} support.`;

  // Convert existing FAQ to AIO format
  const faqItems = device.faq.map(item => ({
    question: item.q,
    answer: item.a
  }));

  // Add device-specific FAQs
  const extendedFAQ = [
    ...faqItems,
    {
      question: `What are the ${device.name} specifications?`,
      answer: `${device.name} specs: ${device.specifications.processor} processor, ${device.specifications.ram} RAM, ${device.specifications.storage} storage, ${device.specifications.resolution} resolution, ${device.specifications.connectivity} connectivity.`
    },
    {
      question: `Which apps work best on ${device.name}?`,
      answer: `Recommended apps for ${device.name}: ${device.recommendedApps.join(', ')}. All support Xtream Codes API and M3U playlists with XtreamCube IPTV.`
    }
  ];

  const totalSetupTime = device.setupSteps.reduce((total, step) => {
    const time = parseInt(step.time);
    return total + time;
  }, 0);

  return (
    <div className="min-h-screen relative">
      <GridBackground />
      <TopBanner />

      <main className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <Button asChild variant="outline" className="mb-8 glass border-primary/30">
            <Link to="/guides/devices">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Devices
            </Link>
          </Button>

          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <Smartphone className="w-10 h-10 text-primary" />
              <h1 className="text-4xl sm:text-5xl font-bold text-white">
                {device.name} IPTV Setup
              </h1>
            </div>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Complete guide to setting up XtreamCube IPTV on {device.name}
            </p>
            <div className="flex items-center justify-center gap-4 mt-4 text-sm text-white/60">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {totalSetupTime} min setup
              </span>
              <span>•</span>
              <span>{device.category}</span>
            </div>
          </motion.div>

          {/* Quick Answer */}
          <QuickAnswerSection answer={quickAnswer} />

          {/* Variants */}
          {device.variants && device.variants.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-8 glass rounded-xl p-6"
              style={{ boxShadow: 'var(--shadow-glass)' }}
            >
              <h3 className="text-lg font-bold text-white mb-3">Compatible Models</h3>
              <div className="flex flex-wrap gap-2">
                {device.variants.map(variant => (
                  <span key={variant} className="text-sm px-3 py-1 bg-primary/20 text-primary rounded-full">
                    {variant}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Setup Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12"
          >
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Step-by-Step Setup Guide
            </h2>

            <div className="space-y-6">
              {device.setupSteps.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                  className="glass rounded-xl p-6"
                  style={{ boxShadow: 'var(--shadow-glass)' }}
                >
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-primary font-bold">{step.step}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-bold text-white">{step.title}</h3>
                        <span className="text-xs text-primary flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {step.time}
                        </span>
                      </div>
                      <p className="text-white/70">{step.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Pros & Cons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <div className="glass rounded-xl p-6" style={{ boxShadow: 'var(--shadow-glass)' }}>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                Advantages
              </h3>
              <ul className="space-y-2">
                {device.pros.map(pro => (
                  <li key={pro} className="flex items-start gap-2 text-white/80 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    {pro}
                  </li>
                ))}
              </ul>
            </div>

            <div className="glass rounded-xl p-6" style={{ boxShadow: 'var(--shadow-glass)' }}>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-yellow-400" />
                Considerations
              </h3>
              <ul className="space-y-2">
                {device.cons.map(con => (
                  <li key={con} className="flex items-start gap-2 text-white/80 text-sm">
                    <XCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    {con}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Troubleshooting */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 glass rounded-xl p-8"
            style={{ boxShadow: 'var(--shadow-glass)' }}
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-primary" />
              Troubleshooting Common Issues
            </h2>
            <div className="space-y-4">
              {device.troubleshooting.map((item, index) => (
                <div key={index} className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-bold text-white mb-2">Problem: {item.problem}</h4>
                  <p className="text-white/70 text-sm">Solution: {item.solution}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* FAQ */}
          <div className="mt-16">
            <GateFAQSection faqItems={extendedFAQ} />
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-16 text-center glass rounded-2xl p-8"
            style={{ boxShadow: 'var(--shadow-glass)' }}
          >
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Stream on {device.name}?
            </h3>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">
              Get your free 24-hour trial and start streaming on {device.name} today
            </p>
            <Button asChild className="bg-primary hover:bg-primary/90 text-white">
              <a href="https://xtreamcube.shop/freetrialiptv/" target="_blank" rel="noopener noreferrer">
                Start Free Trial
              </a>
            </Button>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
